import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import axios from "axios";
import {
  Button,
  Drawer,
  Empty,
  Input,
  message,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import { PictureOutlined } from "@ant-design/icons";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import { format } from "date-fns";

import { normalizeAdminChatThreadKey, normalizeOperatorEmail } from "../utils/operatorEmail";
import { COLORS } from "../styled/basic";
import type { AdminOperatorChatMessageEvent } from "../shared/interfaces/socket";
import {
  getAdminChatThreads,
  getAdminChatMessages,
  postAdminChatMessage,
  presignAdminChatAttachmentUpload,
  postAdminChatCloseThread,
  postAdminChatReopenThread,
  postAdminChatDeleteThread,
  markAdminChatRead,
  postAdminChatMessageReaction,
  type AdminChatThreadSummary,
  type AdminChatMessageRow,
  type AdminChatReactionKind,
} from "../axios/routes/operatorAdminChat";
import { listAccessDirectory, OperatorAccessRecord } from "../axios/routes/access";
import { getError } from "../axios/utils";
import type { IAxiosError } from "../axios/interfaces";

const { Text, Title } = Typography;

const MAX_ADMIN_CHAT_IMAGES = 3;
const MAX_ADMIN_CHAT_IMAGE_MB = 8;

function fileFromUploadEntry(entry: UploadFile): File | undefined {
  if (entry.originFileObj instanceof File) {
    return entry.originFileObj;
  }
  const legacy = entry as UploadFile & { file?: File };
  return legacy.file instanceof File ? legacy.file : undefined;
}

async function uploadAdminChatAttachments(
  viewerEmail: string,
  fileList: UploadFile[],
): Promise<string[]> {
  const files = fileList
    .map((entry) => fileFromUploadEntry(entry))
    .filter((f): f is File => Boolean(f));

  const refs: string[] = [];
  await files.reduce<Promise<void>>(
    (prev, file) => prev.then(async () => {
      const presign = await presignAdminChatAttachmentUpload(viewerEmail, file.type);
      const putRes = await axios.put(presign.uploadURL, file, {
        headers: { "Content-Type": file.type },
      });
      if (putRes.status < 200 || putRes.status >= 300) {
        throw new Error("Image upload failed. Please try again.");
      }
      // Server accepts S3 keys (preferred) or HTTPS URLs from presign.
      const ref = presign.uploadKey || presign.imageShortUrl;
      if (!ref) {
        throw new Error("Upload succeeded but attachment reference was missing.");
      }
      refs.push(ref);
    }),
    Promise.resolve(),
  );
  return refs;
}

function AdminChatMessageAttachments({
  urls,
  mine,
  hasBody,
}: {
  urls: string[];
  mine: boolean;
  hasBody: boolean;
}): JSX.Element | null {
  if (!urls.length) {
    return null;
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        marginTop: hasBody ? 8 : 0,
      }}
    >
      {urls.map((url) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={url}
            alt="Chat attachment"
            style={{
              maxWidth: 220,
              maxHeight: 220,
              borderRadius: 8,
              display: "block",
              border: mine ? "1px solid rgba(255,255,255,0.35)" : "1px solid #e2e8f0",
            }}
          />
        </a>
      ))}
    </div>
  );
}

const REACTION_EMOJI: Record<AdminChatReactionKind, string> = {
  thumbsUp: "👍",
  thumbsDown: "👎",
  emphasize: "‼️",
  haha: "😂",
  question: "❓",
};

const REACTION_PICKER: { kind: AdminChatReactionKind; title: string }[] = [
  { kind: "thumbsUp", title: "Thumbs up" },
  { kind: "thumbsDown", title: "Thumbs down" },
  { kind: "emphasize", title: "Emphasize" },
  { kind: "haha", title: "Ha ha" },
  { kind: "question", title: "Question" },
];

function adminChatThreadKeysMatch(a: string | null, b: string | null): boolean {
  if (a == null || b == null) return false;
  return normalizeAdminChatThreadKey(a) === normalizeAdminChatThreadKey(b);
}

type Props = {
  open: boolean;
  onClose: () => void;
  viewerEmail: string;
  isSuperAdmin: boolean;
  /** Select this thread when the drawer opens (e.g. from notification). */
  focusThreadKey?: string | null;
  onConsumedFocus?: () => void;
  onActivity?: () => void;
  /**
   * When set (e.g. Games dashboard "Chat"), operator outbound messages use this game
   * (must be assigned). Cleared when the parent clears it (e.g. drawer closed).
   */
  operatorOutboundGameId?: string | null;
  /** Super admin opened chat from the Games row Chat button — show Start Chat emphasis. */
  showStartChatCta?: boolean;
};

export function OperatorAdminChatDrawer({
  open,
  onClose,
  viewerEmail,
  isSuperAdmin,
  focusThreadKey,
  onConsumedFocus,
  onActivity,
  operatorOutboundGameId = null,
  showStartChatCta = false,
}: Props): JSX.Element {
  const [threads, setThreads] = useState<AdminChatThreadSummary[]>([]);
  const [groupBy, setGroupBy] = useState<"operator" | "game">("operator");
  const [selectedThreadKey, setSelectedThreadKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<AdminChatMessageRow[]>([]);
  const latestMessagesRef = useRef<AdminChatMessageRow[]>([]);
  latestMessagesRef.current = messages;
  const [input, setInput] = useState("");
  const [pendingImages, setPendingImages] = useState<UploadFile[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [closingThread, setClosingThread] = useState(false);
  const [deletingThread, setDeletingThread] = useState(false);
  const [reactionBusyMessageId, setReactionBusyMessageId] = useState<string | null>(null);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [operatorDirectory, setOperatorDirectory] = useState<OperatorAccessRecord[]>([]);
  const [newChatRecipients, setNewChatRecipients] = useState<string[]>([]);
  /** Message whose tapback emoji row is shown; set by clicking that message bubble. */
  const [tapbackMessageId, setTapbackMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  /**
   * Super admin: deep-link / Games "Chat" sets focusThreadKey once, then parent clears it.
   * The thread may not exist in GET /threads yet. Without this pin, auto-select logic overwrites
   * the intended key with threads[0] or null. State (not a ref) so the composer re-enables when
   * only the pin holds the key.
   */
  const [superAdminPinnedThreadKey, setSuperAdminPinnedThreadKey] = useState<string | null>(null);

  const focusComposer = useCallback(() => {
    const el = document.getElementById("operator-admin-chat-composer");
    if (el instanceof HTMLTextAreaElement) {
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);
  const selectedThreadKeyRef = useRef<string | null>(null);
  selectedThreadKeyRef.current = selectedThreadKey;
  const messagesLoadSeqRef = useRef(0);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const refreshThreads = useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (!viewerEmail) return;
      if (!opts?.quiet) {
        setLoadingThreads(true);
      }
      try {
        const t = await getAdminChatThreads(viewerEmail);
        setThreads(t);
      } finally {
        if (!opts?.quiet) {
          setLoadingThreads(false);
        }
      }
    },
    [viewerEmail],
  );

  const selectThreadFromList = useCallback((threadKey: string) => {
    setSuperAdminPinnedThreadKey(null);
    setSelectedThreadKey(threadKey);
  }, []);

  useEffect(() => {
    if (open && viewerEmail) {
      refreshThreads();
    }
  }, [open, viewerEmail, refreshThreads]);

  useEffect(() => {
    if (!open || !viewerEmail || !isSuperAdmin) {
      return;
    }
    setDirectoryLoading(true);
    listAccessDirectory(viewerEmail)
      .then((rows) => {
        setOperatorDirectory(rows);
      })
      .finally(() => setDirectoryLoading(false));
  }, [open, viewerEmail, isSuperAdmin]);

  useEffect(() => {
    if (!open) {
      setSuperAdminPinnedThreadKey(null);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !focusThreadKey) {
      return;
    }
    const k = normalizeAdminChatThreadKey(focusThreadKey);
    if (k) {
      setSelectedThreadKey(k);
      if (isSuperAdmin) {
        setSuperAdminPinnedThreadKey(k);
      }
    }
    onConsumedFocus?.();
  }, [open, focusThreadKey, onConsumedFocus, isSuperAdmin]);

  const superAdminEffectiveThreadKey = useMemo(() => {
    if (!isSuperAdmin) {
      return selectedThreadKey;
    }
    const raw = selectedThreadKey || superAdminPinnedThreadKey;
    if (!raw) {
      return null;
    }
    const k = normalizeAdminChatThreadKey(raw);
    return k || null;
  }, [isSuperAdmin, selectedThreadKey, superAdminPinnedThreadKey]);

  /**
   * Align selection with the exact threadKey from the API list (legacy rows may differ only by
   * email casing).
   */
  useEffect(() => {
    if (!selectedThreadKey || threads.length === 0) {
      return;
    }
    const row = threads.find((t) => adminChatThreadKeysMatch(t.threadKey, selectedThreadKey));
    if (row && row.threadKey !== selectedThreadKey) {
      setSelectedThreadKey(row.threadKey);
    }
  }, [threads, selectedThreadKey]);

  useEffect(() => {
    setTapbackMessageId(null);
  }, [selectedThreadKey, superAdminPinnedThreadKey, open]);

  const loadMessages = useCallback(
    async (threadKey: string, opts?: { quiet?: boolean }) => {
      const canonicalKey = normalizeAdminChatThreadKey(threadKey);
      if (!viewerEmail || !canonicalKey) return;
      const quiet = Boolean(opts?.quiet);

      if (quiet) {
        try {
          const prevIds = latestMessagesRef.current.map((r) => r._id).join(",");
          const rows = await getAdminChatMessages(viewerEmail, canonicalKey, 120);
          if (!adminChatThreadKeysMatch(canonicalKey, selectedThreadKeyRef.current)) {
            return;
          }
          const nextIds = rows.map((r) => r._id).join(",");
          setMessages(rows);
          if (nextIds !== prevIds) {
            window.setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 50);
          }
          onActivityRef.current?.();
        } catch {
          // Background refresh: ignore network errors so the UI keeps last good data.
        }
        return;
      }

      messagesLoadSeqRef.current += 1;
      const seq = messagesLoadSeqRef.current;
      setLoadingMessages(true);
      try {
        const rows = await getAdminChatMessages(viewerEmail, canonicalKey, 120);
        if (seq !== messagesLoadSeqRef.current) {
          return;
        }
        if (!adminChatThreadKeysMatch(canonicalKey, selectedThreadKeyRef.current)) {
          return;
        }
        setMessages(rows);
        await markAdminChatRead(viewerEmail, canonicalKey).catch(() => undefined);
        onActivityRef.current?.();
        window.setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      } catch (e: unknown) {
        const errMsg = e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
        if (typeof errMsg === "string") {
          message.error(errMsg);
        } else {
          message.error("Could not load messages");
        }
      } finally {
        if (seq === messagesLoadSeqRef.current) {
          setLoadingMessages(false);
        }
      }
    },
    [viewerEmail],
  );

  useEffect(() => {
    if (!open || !viewerEmail) {
      return;
    }
    if (!selectedThreadKey) {
      setMessages([]);
      return;
    }
    setMessages([]);
    loadMessages(selectedThreadKey);
  }, [open, viewerEmail, selectedThreadKey, loadMessages]);

  /** Poll messages while the drawer is open so new lines appear without closing the chat. */
  useEffect(() => {
    if (!open || !viewerEmail || !selectedThreadKey) {
      return undefined;
    }
    const key = selectedThreadKey;
    const id = window.setInterval(() => {
      loadMessages(key, { quiet: true }).catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(id);
  }, [open, viewerEmail, selectedThreadKey, loadMessages]);

  useEffect(() => {
    if (!open || !viewerEmail || isSuperAdmin) {
      return;
    }
    if (focusThreadKey) {
      return;
    }
    if (threads.length > 0) {
      const stillValid = Boolean(
        selectedThreadKey
          && threads.some((t) => adminChatThreadKeysMatch(t.threadKey, selectedThreadKey)),
      );
      if (!stillValid) {
        setSelectedThreadKey(threads[0].threadKey);
      }
      return;
    }
    // Do not use a synthetic key like operator::_ — messages live under operator::<gameId>.
    // While threads are still loading, keep selection (e.g. focusThread from notification).
    if (!loadingThreads) {
      setSelectedThreadKey(null);
    }
  }, [open, isSuperAdmin, viewerEmail, threads, selectedThreadKey, focusThreadKey, loadingThreads]);

  useEffect(() => {
    if (!open || !viewerEmail || !isSuperAdmin) {
      return;
    }
    if (focusThreadKey) {
      return;
    }
    const pinned = superAdminPinnedThreadKey;
    if (pinned) {
      const canon = normalizeAdminChatThreadKey(pinned);
      if (
        !selectedThreadKey
        || !adminChatThreadKeysMatch(selectedThreadKey, canon)
      ) {
        setSelectedThreadKey(canon);
      }
      return;
    }
    if (threads.length === 0) {
      if (!loadingThreads) {
        setSelectedThreadKey(null);
      }
      return;
    }
    const stillValid = Boolean(
      selectedThreadKey
        && threads.some((t) => adminChatThreadKeysMatch(t.threadKey, selectedThreadKey)),
    );
    if (!stillValid) {
      setSelectedThreadKey(threads[0].threadKey);
    }
  }, [
    open,
    isSuperAdmin,
    viewerEmail,
    threads,
    selectedThreadKey,
    focusThreadKey,
    loadingThreads,
    superAdminPinnedThreadKey,
  ]);

  useEffect(() => {
    if (!open || !viewerEmail) {
      return () => undefined;
    }

    const onChatMessage = (ev: Event): void => {
      const { detail } = ev as CustomEvent<AdminOperatorChatMessageEvent>;
      const row = detail?.message;
      if (!row) {
        return;
      }
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m._id === row._id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...row };
          return next;
        }
        if (!adminChatThreadKeysMatch(row.threadKey, selectedThreadKeyRef.current)) {
          return prev;
        }
        return [...prev, row];
      });
      refreshThreads({ quiet: true }).catch(() => undefined);
    };

    const onThreadState = (ev: Event): void => {
      const { detail } = ev as CustomEvent<{
        threadKey: string;
        closed: boolean;
        deleted?: boolean;
      }>;
      if (!detail?.threadKey) {
        return;
      }
      if (detail.deleted) {
        setThreads((prev) => prev.filter((t) => (
          !adminChatThreadKeysMatch(t.threadKey, detail.threadKey)
        )));
        if (adminChatThreadKeysMatch(selectedThreadKeyRef.current, detail.threadKey)) {
          setSelectedThreadKey(null);
          setMessages([]);
        }
        refreshThreads({ quiet: true }).catch(() => undefined);
        return;
      }
      if (!isSuperAdmin && detail.closed) {
        setThreads((prev) => prev.filter((t) => (
          !adminChatThreadKeysMatch(t.threadKey, detail.threadKey)
        )));
        if (adminChatThreadKeysMatch(selectedThreadKeyRef.current, detail.threadKey)) {
          setSelectedThreadKey(null);
          setMessages([]);
        }
      } else {
        setThreads((prev) => prev.map((t) => {
          return adminChatThreadKeysMatch(t.threadKey, detail.threadKey)
            ? { ...t, closed: detail.closed }
            : t;
        }));
      }
      refreshThreads({ quiet: true }).catch(() => undefined);
    };

    window.addEventListener(
      "momento:admin-operator-chat-message",
      onChatMessage as EventListener,
    );
    window.addEventListener(
      "momento:admin-chat-thread-state",
      onThreadState as EventListener,
    );
    return () => {
      window.removeEventListener(
        "momento:admin-operator-chat-message",
        onChatMessage as EventListener,
      );
      window.removeEventListener(
        "momento:admin-chat-thread-state",
        onThreadState as EventListener,
      );
    };
  }, [open, viewerEmail, refreshThreads, isSuperAdmin]);

  const groupedByOperator = useMemo(() => {
    const m = new Map<string, AdminChatThreadSummary[]>();
    threads.forEach((t) => {
      const k = t.operatorEmail;
      const arr = m.get(k) || [];
      arr.push(t);
      m.set(k, arr);
    });
    return m;
  }, [threads]);

  const groupedByGame = useMemo(() => {
    const m = new Map<string, AdminChatThreadSummary[]>();
    threads.forEach((t) => {
      const k = t.gameId || "__none__";
      const arr = m.get(k) || [];
      arr.push(t);
      m.set(k, arr);
    });
    return m;
  }, [threads]);

  const selectedThread = useMemo(
    () => threads.find((t) => adminChatThreadKeysMatch(t.threadKey, selectedThreadKey)),
    [threads, selectedThreadKey],
  );
  const availableOperatorRecipients = useMemo(() => {
    const me = normalizeOperatorEmail(viewerEmail);
    return operatorDirectory
      .filter((r) => normalizeOperatorEmail(r.email) !== me)
      .filter((r) => (r.role || "operator") !== "superAdmin")
      .sort((a, b) => {
        const aName = (a.name || a.firstName || a.email).toLowerCase();
        const bName = (b.name || b.firstName || b.email).toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [operatorDirectory, viewerEmail]);

  const beforeUploadAdminChatImage = (file: RcFile) => {
    const okType = ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type);
    if (!okType) {
      message.error("Please use JPG, PNG, WebP, or GIF.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_ADMIN_CHAT_IMAGE_MB * 1024 * 1024) {
      message.error(`Each image must be ${MAX_ADMIN_CHAT_IMAGE_MB} MB or smaller.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const canSendMessage = Boolean(
    input.trim() || pendingImages.length,
  ) && !(isSuperAdmin && !superAdminEffectiveThreadKey && !newChatRecipients.length);

  const sendMessage = async (): Promise<void> => {
    const body = input.trim();
    if ((!body && !pendingImages.length) || !viewerEmail) {
      return;
    }
    const threadKeyForSuper = normalizeAdminChatThreadKey(
      selectedThreadKey || superAdminPinnedThreadKey || "",
    );
    if (isSuperAdmin && !threadKeyForSuper) {
      if (!newChatRecipients.length) {
        message.warning("Select at least one operator to start chat.");
        return;
      }
    }

    setSending(true);
    try {
      const attachmentUrls = await uploadAdminChatAttachments(viewerEmail, pendingImages);
      if (pendingImages.length > 0 && !attachmentUrls.length) {
        message.error("Could not read selected images. Remove and re-attach them, then try again.");
        return;
      }
      const payloadBase = {
        body,
        ...(attachmentUrls.length ? { attachmentUrls } : {}),
      };
      let newMsg: AdminChatMessageRow;
      if (isSuperAdmin) {
        if (threadKeyForSuper) {
          newMsg = await postAdminChatMessage(viewerEmail, {
            ...payloadBase,
            threadKey: threadKeyForSuper,
          });
        } else {
          const sends = await Promise.allSettled(
            newChatRecipients.map((operatorEmail) => postAdminChatMessage(viewerEmail, {
              ...payloadBase,
              operatorEmail,
            })),
          );
          const successes = sends
            .filter((s): s is PromiseFulfilledResult<AdminChatMessageRow> => s.status === "fulfilled")
            .map((s) => s.value);
          if (!successes.length) {
            message.error("Could not start chats for selected operators.");
            return;
          }
          if (successes.length < newChatRecipients.length) {
            message.warning(`Started ${successes.length} of ${newChatRecipients.length} chats.`);
          } else {
            message.success(`Started ${successes.length} chats.`);
          }
          newMsg = successes[0];
          setNewChatRecipients([]);
        }
      } else {
        newMsg = await postAdminChatMessage(
          viewerEmail,
          operatorOutboundGameId
            ? { ...payloadBase, gameId: operatorOutboundGameId }
            : payloadBase,
        );
      }
      setInput("");
      setPendingImages([]);
      setSelectedThreadKey(newMsg.threadKey);
      setSuperAdminPinnedThreadKey(null);
      await refreshThreads({ quiet: true });
      await loadMessages(newMsg.threadKey);
      window.setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);
      onActivity?.();
    } catch (e: unknown) {
      if (e && typeof e === "object" && "response" in e) {
        const { response } = e as IAxiosError;
        message.error(getError(response ?? null));
      } else if (e instanceof Error && e.message) {
        message.error(e.message);
      } else {
        message.error("Failed to send message");
      }
    } finally {
      setSending(false);
    }
  };

  const sendReaction = useCallback(
    async (m: AdminChatMessageRow, kind: AdminChatReactionKind) => {
      if (!viewerEmail) {
        return;
      }
      setReactionBusyMessageId(m._id);
      try {
        const updated = await postAdminChatMessageReaction(viewerEmail, m._id, kind);
        setMessages((prev) => {
          const idx = prev.findIndex((x) => x._id === updated._id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = updated;
            return next;
          }
          return prev;
        });
      } catch (e: unknown) {
        const errMsg = e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
        message.error(typeof errMsg === "string" ? errMsg : "Could not update reaction");
      } finally {
        setReactionBusyMessageId(null);
      }
    },
    [viewerEmail],
  );

  const onCloseConversation = async (): Promise<void> => {
    if (!viewerEmail || !isSuperAdmin) {
      return;
    }
    const key = superAdminEffectiveThreadKey;
    if (!key) {
      return;
    }
    setClosingThread(true);
    try {
      await postAdminChatCloseThread(viewerEmail, key);
      setThreads((prev) => prev.map((t) => {
        const match = adminChatThreadKeysMatch(t.threadKey, key);
        return match ? { ...t, closed: true } : t;
      }));
    } catch (e: unknown) {
      const errMsg = e && typeof e === "object" && "response" in e
        ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      message.error(typeof errMsg === "string" ? errMsg : "Could not close conversation");
    } finally {
      setClosingThread(false);
    }
  };

  const onReopenConversation = async (): Promise<void> => {
    if (!viewerEmail || !isSuperAdmin) {
      return;
    }
    const key = superAdminEffectiveThreadKey;
    if (!key) {
      return;
    }
    setClosingThread(true);
    try {
      await postAdminChatReopenThread(viewerEmail, key);
      setThreads((prev) => prev.map((t) => {
        const match = adminChatThreadKeysMatch(t.threadKey, key);
        return match ? { ...t, closed: false } : t;
      }));
    } catch (e: unknown) {
      const errMsg = e && typeof e === "object" && "response" in e
        ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      message.error(typeof errMsg === "string" ? errMsg : "Could not reopen conversation");
    } finally {
      setClosingThread(false);
    }
  };

  const onDeleteConversation = async (): Promise<void> => {
    if (!viewerEmail || !isSuperAdmin) {
      return;
    }
    const key = superAdminEffectiveThreadKey;
    if (!key) {
      return;
    }
    setDeletingThread(true);
    try {
      await postAdminChatDeleteThread(viewerEmail, key);
      setSuperAdminPinnedThreadKey(null);
      setThreads((prev) => prev.filter((t) => !adminChatThreadKeysMatch(t.threadKey, key)));
      setSelectedThreadKey(null);
      setMessages([]);
      message.success("Conversation deleted");
      await refreshThreads({ quiet: true });
      onActivity?.();
    } catch (e: unknown) {
      const errMsg = e && typeof e === "object" && "response" in e
        ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      message.error(typeof errMsg === "string" ? errMsg : "Could not delete conversation");
    } finally {
      setDeletingThread(false);
    }
  };

  const renderThreadList = (): JSX.Element => {
    if (loadingThreads && threads.length === 0) {
      return <Spin style={{ margin: 24 }} />;
    }
    if (threads.length === 0 && !isSuperAdmin) {
      return (
        <Empty
          description="No history yet — send a message to reach super admins."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: 16 }}
        />
      );
    }
    if (threads.length === 0) {
      return (
        <Empty
          description="No operator chat threads yet."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: 16 }}
        />
      );
    }

    if (!isSuperAdmin) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} className="admin-chat-thread-list">
          {threads.map((t) => (
            <button
              key={t.threadKey}
              type="button"
              onClick={() => selectThreadFromList(t.threadKey)}
              style={{
                textAlign: "left",
                padding: "10px 12px",
                borderRadius: 8,
                border: adminChatThreadKeysMatch(selectedThreadKey, t.threadKey)
                  ? `2px solid ${COLORS.BLUE_MAIN}`
                  : "1px solid #e8e8e8",
                background: adminChatThreadKeysMatch(selectedThreadKey, t.threadKey) ? "#f0f7ff" : "#fff",
                cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {t.gameLabel || "General"}
                {t.closed ? (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "#94a3b8" }}>(closed)</span>
                ) : null}
              </div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                {format(new Date(t.lastMessageAt), "MMM d, h:mm a")}
                {t.unread ? (
                  <span style={{ color: COLORS.BLUE_MAIN, marginLeft: 8 }}>● New</span>
                ) : null}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#555",
                  marginTop: 6,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.lastPreview}
              </div>
            </button>
          ))}
        </div>
      );
    }

    const groups = groupBy === "operator" ? groupedByOperator : groupedByGame;
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {sortedKeys.map((key) => {
          const list = groups.get(key) || [];
          const header = groupBy === "operator"
            ? key
            : (list[0]?.gameLabel || "General");
          return (
            <div key={key}>
              <Text strong style={{ fontSize: 12, color: "#64748b" }}>
                {groupBy === "operator"
                  ? `Operator: ${
                    (list[0]?.operatorDisplayName || header)
                  }`
                  : `Game: ${header}`}
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                {list.map((t) => (
                  <button
                    key={t.threadKey}
                    type="button"
                    onClick={() => selectThreadFromList(t.threadKey)}
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: adminChatThreadKeysMatch(selectedThreadKey, t.threadKey)
                        ? `2px solid ${COLORS.BLUE_MAIN}`
                        : "1px solid #e8e8e8",
                      background: adminChatThreadKeysMatch(selectedThreadKey, t.threadKey) ? "#f0f7ff" : "#fff",
                      cursor: "pointer",
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    <div style={{ fontSize: 12 }}>
                      {groupBy === "operator" ? t.gameLabel : (t.operatorDisplayName || t.operatorEmail)}
                      {t.closed ? (
                        <span style={{ marginLeft: 6, color: "#94a3b8" }}>· closed</span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 11, color: "#888" }}>
                      {format(new Date(t.lastMessageAt), "MMM d, h:mm a")}
                      {t.unread ? (
                        <span style={{ color: COLORS.BLUE_MAIN, marginLeft: 8 }}>●</span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Drawer
      title={(
        <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>
          {isSuperAdmin ? "Admin chat (operators)" : "Chat with super admins"}
        </span>
      )}
      placement="right"
      width={720}
      onClose={onClose}
      visible={open}
      destroyOnClose
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }}
    >
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
        {!isSuperAdmin ? (
          <Text type="secondary" style={{ fontSize: 13, display: "block" }}>
            New messages use your current or next game assignment as context
            {" "}
            (ongoing, then upcoming, then most recent).
          </Text>
        ) : null}
        {isSuperAdmin ? (
          <Radio.Group
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            style={{ marginTop: 8 }}
          >
            <Radio.Button value="operator">By operator</Radio.Button>
            <Radio.Button value="game">By game</Radio.Button>
          </Radio.Group>
        ) : null}
        {isSuperAdmin && showStartChatCta ? (
          <div
            style={{
              marginTop: 12,
              padding: "12px 14px",
              background: "#e0f2fe",
              borderRadius: 8,
              border: "1px solid #7dd3fc",
            }}
          >
            <Text style={{ fontSize: 13, display: "block", marginBottom: 10, color: "#0c4a6e" }}>
              You opened this chat from a game on the Games dashboard. Use this to start or continue
              the admin chat session for that matchup (same operator and game).
            </Text>
            <Button
              type="primary"
              onClick={() => {
                let key = selectedThreadKey
                  ? normalizeAdminChatThreadKey(selectedThreadKey)
                  : "";
                if (!key && superAdminPinnedThreadKey) {
                  key = normalizeAdminChatThreadKey(superAdminPinnedThreadKey);
                  setSelectedThreadKey(key);
                }
                if (!key) {
                  message.warning(
                    "Could not resolve this game’s chat thread. Open chat again from the Games row.",
                  );
                  return;
                }
                focusComposer();
              }}
              style={{ background: COLORS.BLUE_MAIN, borderColor: COLORS.BLUE_MAIN }}
            >
              Start chat for this game
            </Button>
          </div>
        ) : null}
        <Button type="link" size="small" onClick={() => refreshThreads()} loading={loadingThreads} style={{ paddingLeft: 0 }}>
          Refresh threads
        </Button>
      </div>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div
          style={{
            width: 260,
            flexShrink: 0,
            borderRight: "1px solid #f0f0f0",
            overflowY: "auto",
            padding: 12,
            background: "#fafafa",
          }}
        >
          {renderThreadList()}
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {selectedThread && isSuperAdmin ? (
            <div
              style={{
                padding: "8px 16px",
                background: "#f8fafc",
                fontSize: 12,
                borderBottom: "1px solid #eee",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Text strong>{selectedThread.gameLabel}</Text>
              <span style={{ color: "#cbd5e1" }}>|</span>
              <Text type="secondary">
                {selectedThread.operatorDisplayName || selectedThread.operatorEmail}
              </Text>
              {selectedThread.closed ? (
                <Text type="warning" style={{ fontSize: 11 }}>
                  Closed
                </Text>
              ) : null}
              <span style={{ flex: 1 }} />
              {selectedThread.closed ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    loading={closingThread}
                    disabled={deletingThread}
                    onClick={() => {
                      onReopenConversation().catch(() => undefined);
                    }}
                  >
                    Reopen conversation
                  </Button>
                  <Popconfirm
                    title={(
                      <span>
                        Permanently delete this conversation? All messages will be removed.
                        {" "}
                        This cannot be undone. The operator will no longer see this thread.
                      </span>
                    )}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={() => {
                      onDeleteConversation().catch(() => undefined);
                    }}
                  >
                    <Button size="small" danger loading={deletingThread} disabled={closingThread}>
                      Delete conversation
                    </Button>
                  </Popconfirm>
                </>
              ) : (
                <Popconfirm
                  title="Close this conversation? It is hidden from the operator until they send a new message (which reopens it) or you reopen it here."
                  okText="Close"
                  cancelText="Cancel"
                  onConfirm={() => {
                    onCloseConversation().catch(() => undefined);
                  }}
                >
                  <Button size="small" danger loading={closingThread} disabled={deletingThread}>
                    Close conversation
                  </Button>
                </Popconfirm>
              )}
            </div>
          ) : null}

          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {loadingMessages ? <Spin /> : null}
            {!loadingMessages && messages.length > 0 ? (
              <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
                Click on message for Tapback Options
              </Text>
            ) : null}
            {!loadingMessages && messages.length === 0 ? (
              (isSuperAdmin ? !superAdminEffectiveThreadKey : !selectedThreadKey) ? (
                <Empty
                  description={
                    isSuperAdmin
                      ? "Select a conversation on the left."
                      : "Send a message below — your game context is chosen automatically."
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : isSuperAdmin ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "28px 16px 8px",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  <Title level={4} style={{ marginBottom: 12, fontWeight: 700 }}>
                    Start Chat
                  </Title>
                  <Text type="secondary" style={{ display: "block", marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>
                    From super admin: send your first message to this operator using the box below.
                  </Text>
                  {showStartChatCta ? (
                    <Text type="secondary" style={{ display: "block", marginBottom: 20, fontSize: 13 }}>
                      You opened this chat from the Games dashboard.
                    </Text>
                  ) : null}
                  <div style={{ maxWidth: 560, margin: "0 auto 16px" }}>
                    <Select
                      mode="multiple"
                      style={{ width: "100%" }}
                      value={newChatRecipients}
                      onChange={(vals) => setNewChatRecipients(vals)}
                      loading={directoryLoading}
                      placeholder="Select one or more operators"
                      optionFilterProp="children"
                      showSearch
                      disabled={sending}
                    >
                      {availableOperatorRecipients.map((op) => (
                        <Select.Option key={op.email} value={op.email}>
                          {(op.name || op.firstName || op.email)}
                          {" - "}
                          {op.email}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    style={{ background: COLORS.BLUE_MAIN, borderColor: COLORS.BLUE_MAIN }}
                    onClick={focusComposer}
                  >
                    Start Chat
                  </Button>
                </div>
              ) : (
                <Empty
                  description="No messages yet — start the conversation."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            ) : (
              messages.map((m) => {
                const mine = (
                  normalizeOperatorEmail(m.fromEmail) === normalizeOperatorEmail(viewerEmail)
                );
                const myReaction = (m.reactions || []).find((r) => {
                  const re = normalizeOperatorEmail(r.fromEmail);
                  const ve = normalizeOperatorEmail(viewerEmail);
                  return re === ve;
                })?.kind ?? null;
                const reactionBusy = reactionBusyMessageId === m._id;
                const tapbackOpen = tapbackMessageId === m._id;
                const groupedByKind: Partial<Record<AdminChatReactionKind, string[]>> = {};
                (m.reactions || []).forEach((r) => {
                  const k = r.kind;
                  const list = groupedByKind[k] ?? (groupedByKind[k] = []);
                  list.push(r.fromDisplayName || r.fromEmail);
                });
                return (
                  <div
                    key={m._id}
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: mine ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: mine ? "flex-end" : "flex-start",
                        width: "100%",
                      }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setTapbackMessageId((id) => (id === m._id ? null : m._id));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setTapbackMessageId((id) => (id === m._id ? null : m._id));
                          }
                        }}
                        style={{
                          maxWidth: "85%",
                          padding: "10px 14px",
                          borderRadius: 12,
                          background: mine ? COLORS.BLUE_MAIN : "#f1f5f9",
                          color: mine ? "#fff" : "#1e293b",
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: 14,
                          lineHeight: 1.45,
                          cursor: "pointer",
                          outline: tapbackOpen ? `2px solid ${mine ? "rgba(255,255,255,0.85)" : COLORS.BLUE_MAIN}` : "none",
                          outlineOffset: 2,
                        }}
                      >
                        <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>
                          {m.fromRole === "superAdmin" ? "Super admin" : "Operator"}
                          {" · "}
                          {m.fromDisplayName || m.fromEmail}
                          {" · "}
                          {format(new Date(m.createdAt), "MMM d, h:mm a")}
                        </div>
                        {m.body ? (
                          <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
                        ) : null}
                        <AdminChatMessageAttachments
                          urls={m.attachmentUrls || []}
                          mine={mine}
                          hasBody={Boolean(m.body)}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        maxWidth: "85%",
                        marginTop: 6,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: mine ? "flex-end" : "flex-start",
                        gap: 4,
                      }}
                    >
                      {(m.reactions?.length ?? 0) > 0 ? (
                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
                          {REACTION_PICKER.map(({ kind }) => {
                            const names = groupedByKind[kind];
                            if (!names?.length) {
                              return null;
                            }
                            return (
                              <span
                                key={kind}
                                style={{ marginRight: 10 }}
                                title={names.join(", ")}
                              >
                                {REACTION_EMOJI[kind]}
                                {" "}
                                {names.join(", ")}
                              </span>
                            );
                          })}
                        </div>
                      ) : null}
                      {tapbackOpen ? (
                        <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
                          {REACTION_PICKER.map(({ kind, title }) => (
                            <Tooltip key={kind} title={title}>
                              <Button
                                type={myReaction === kind ? "primary" : "text"}
                                size="small"
                                disabled={reactionBusy}
                                style={{
                                  fontSize: 16,
                                  padding: "0 6px",
                                  minWidth: 36,
                                }}
                                onClick={() => {
                                  sendReaction(m, kind).catch(() => undefined);
                                }}
                              >
                                {REACTION_EMOJI[kind]}
                              </Button>
                            </Tooltip>
                          ))}
                        </Space>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ padding: 12, borderTop: "1px solid #f0f0f0", flexShrink: 0 }}>
            {pendingImages.length > 0 ? (
              <Upload
                listType="picture-card"
                fileList={pendingImages}
                onChange={({ fileList }) => setPendingImages(fileList)}
                beforeUpload={beforeUploadAdminChatImage}
                multiple
                maxCount={MAX_ADMIN_CHAT_IMAGES}
                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                style={{ marginBottom: 8 }}
              />
            ) : null}
            <Input.TextArea
              id="operator-admin-chat-composer"
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                isSuperAdmin && superAdminEffectiveThreadKey && !messages.length
                  ? "Start here as super admin…"
                  : (isSuperAdmin ? "Reply to this operator…" : "Message all super admins…")
              }
              disabled={
                sending
                || (isSuperAdmin && !superAdminEffectiveThreadKey && !newChatRecipients.length)
              }
              style={{ fontFamily: "'Montserrat', sans-serif", marginBottom: 8 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  sendMessage().catch(() => undefined);
                }
              }}
            />
            <Space>
              <Upload
                fileList={pendingImages}
                onChange={({ fileList }) => setPendingImages(fileList)}
                beforeUpload={beforeUploadAdminChatImage}
                multiple
                maxCount={MAX_ADMIN_CHAT_IMAGES}
                showUploadList={false}
                disabled={
                  sending
                  || pendingImages.length >= MAX_ADMIN_CHAT_IMAGES
                  || (isSuperAdmin && !superAdminEffectiveThreadKey && !newChatRecipients.length)
                }
              >
                <Tooltip title="Attach image (JPG, PNG, WebP, GIF)">
                  <Button
                    icon={<PictureOutlined />}
                    disabled={
                      sending
                      || pendingImages.length >= MAX_ADMIN_CHAT_IMAGES
                      || (
                        isSuperAdmin
                        && !superAdminEffectiveThreadKey
                        && !newChatRecipients.length
                      )
                    }
                  />
                </Tooltip>
              </Upload>
              <Button
                type="primary"
                onClick={() => {
                  sendMessage().catch(() => undefined);
                }}
                loading={sending}
                disabled={!canSendMessage}
                style={{ background: COLORS.BLUE_MAIN, borderColor: COLORS.BLUE_MAIN }}
              >
                Send
              </Button>
            </Space>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
